import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth.js';
import { setTokens, loadStoredTokens, setApiToken, setOnUnauthorized } from '../api/client.js';

const AuthContext = createContext(null);

/** Decode the payload of a JWT without verifying the signature. */
function decodeJwt(token) {
  try {
    const [, payload] = String(token).split('.');
    if (!payload) return null;
    // base64url → base64 (browser atob handles standard base64).
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '=='.slice((b64.length + 2) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** True if the JWT's `exp` claim is in the past (with 5s skew). */
function isExpired(token) {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now() - 5_000;
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  // ── KEY: starts true so dependent components wait for bootstrap ──
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    setTokens(null);
    setApiToken(null);
    setUser(null);
    setRoles([]);
    setPermissions([]);
  }, []);

  // ── Bootstrap: validate stored token, refresh if expired, fetch /me ──
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setOnUnauthorized(() => {
        clear();
        navigate('/login', { replace: true });
      });

      const { accessToken, refreshToken } = loadStoredTokens();
      if (!accessToken) {
        if (!cancelled) setLoading(false);
        return;
      }

      // If the access token has already expired, attempt refresh BEFORE
      // any /me call to avoid the 401-and-retry round trip.
      let liveToken = accessToken;
      if (isExpired(accessToken) && refreshToken) {
        try {
          const refreshed = await authApi.refresh(refreshToken);
          if (!refreshed?.accessToken) throw new Error('No accessToken in refresh response');
          setTokens(refreshed); // also persists to localStorage
          liveToken = refreshed.accessToken;
        } catch {
          if (!cancelled) {
            clear();
            setLoading(false);
          }
          return;
        }
      }

      // Mirror the live token into the in-memory Axios store BEFORE any
      // request fires — eliminates the previous race where API calls
      // shipped without an Authorization header on hard navigation.
      setApiToken(liveToken);

      try {
        const data = await authApi.me();
        if (cancelled) return;
        setUser(data.user);
        setRoles(data.roles || []);
        setPermissions(data.permissions || []);
      } catch {
        if (!cancelled) clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [clear, navigate]);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    if (data?.mfaRequired) {
      return { mfaRequired: true, challengeId: data.challengeId };
    }
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setApiToken(data.accessToken);
    setUser(data.user);
    const me = await authApi.me();
    setRoles(me.roles || []);
    setPermissions(me.permissions || []);
    return { mfaRequired: false };
  }, []);

  const verifyMfa = useCallback(async (challengeId, code) => {
    const data = await authApi.verifyMfa(challengeId, code);
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setApiToken(data.accessToken);
    setUser(data.user);
    const me = await authApi.me();
    setRoles(me.roles || []);
    setPermissions(me.permissions || []);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('bos.refresh');
    try {
      await authApi.logout(refreshToken);
    } catch {
      // ignore
    }
    clear();
    navigate('/login', { replace: true });
  }, [clear, navigate]);

  const can = useCallback(
    (resource, action) => {
      if (roles.includes('super_admin')) return true;
      return permissions.some((p) => p.resource === resource && p.action === action);
    },
    [roles, permissions]
  );

  const value = useMemo(
    () => ({
      user,
      roles,
      permissions,
      loading,
      isLoading: loading,
      isAuthenticated: !!user,
      login,
      verifyMfa,
      logout,
      can,
    }),
    [user, roles, permissions, loading, login, verifyMfa, logout, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
